// LearningPlanPreview Component
// ==============================
// Displays and allows editing of a generated learning plan before approval
// Feature #82 - Learning plan preview with inline editing

import React, { useState, useCallback } from 'react';
import type { SuggestedLearningPlan, SuggestedPlanTopic } from '../../services/annaTextChat';
import Button from '../ui/Button';
import Card from '../ui/Card';

interface LearningPlanPreviewProps {
  /** The learning plan to display */
  plan: SuggestedLearningPlan;
  /** Callback when plan is approved */
  onApprove: (plan: SuggestedLearningPlan) => void;
  /** Callback to return to Anna chat for refinement */
  onEditWithAnna: () => void;
  /** Optional callback when plan is cancelled */
  onCancel?: () => void;
  /** Loading state during approval */
  isApproving?: boolean;
}

// Editable input component
interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
}

const EditableText: React.FC<EditableTextProps> = ({
  value,
  onChange,
  className = '',
  placeholder = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleBlur = () => {
    setIsEditing(false);
    if (editValue.trim() && editValue !== value) {
      onChange(editValue.trim());
    } else {
      setEditValue(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`border border-primary-400 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 ${className}`}
        autoFocus
        placeholder={placeholder}
      />
    );
  }

  return (
    <span
      onClick={() => setIsEditing(true)}
      className={`cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 -mx-1 ${className}`}
      title="Klik for at redigere"
    >
      {value || placeholder}
    </span>
  );
};

// Topic card component
interface TopicCardProps {
  topic: SuggestedPlanTopic;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate: (topic: SuggestedPlanTopic) => void;
  onDelete: () => void;
}

const TopicCard: React.FC<TopicCardProps> = ({
  topic,
  index,
  isExpanded,
  onToggle,
  onUpdate,
  onDelete,
}) => {
  // Update topic title
  const handleTitleChange = (newTitle: string) => {
    onUpdate({ ...topic, title: newTitle });
  };

  // Update subtopic title
  const handleSubtopicTitleChange = (subIndex: number, newTitle: string) => {
    const newSubtopics = [...(topic.subtopics || [])];
    newSubtopics[subIndex] = { ...newSubtopics[subIndex], title: newTitle };
    onUpdate({ ...topic, subtopics: newSubtopics });
  };

  // Delete subtopic
  const handleDeleteSubtopic = (subIndex: number) => {
    const newSubtopics = (topic.subtopics || []).filter((_, i) => i !== subIndex);
    onUpdate({ ...topic, subtopics: newSubtopics });
  };

  // Estimated sessions (based on duration)
  const estimatedSessions = topic.estimatedDuration
    ? Math.ceil(topic.estimatedDuration / 30) // ~30 min per session
    : topic.subtopics?.length || 1;

  return (
    <Card
      padding="none"
      className="overflow-hidden border border-gray-200 dark:border-gray-700"
    >
      {/* Topic Header */}
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        onClick={onToggle}
      >
        {/* Expand/Collapse Icon */}
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>

        {/* Topic Number */}
        <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
            {index + 1}
          </span>
        </div>

        {/* Topic Title */}
        <div className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
          <EditableText
            value={topic.title}
            onChange={handleTitleChange}
            className="font-medium text-neutral-dark dark:text-white"
          />
        </div>

        {/* Topic Stats */}
        <div className="flex items-center gap-4 text-sm text-neutral-medium flex-shrink-0">
          <span className="flex items-center gap-1">
            📚 {topic.subtopics?.length || 0} underemner
          </span>
          <span className="flex items-center gap-1">
            ⏱️ ~{estimatedSessions} samtale{estimatedSessions !== 1 ? 'r' : ''}
          </span>
        </div>

        {/* Delete Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
          title="Slet emne"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700">
          {/* Description */}
          {topic.description && (
            <p className="text-sm text-neutral-medium mt-3 mb-4">{topic.description}</p>
          )}

          {/* Key Concepts */}
          {topic.keyConcepts && topic.keyConcepts.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-medium text-neutral-medium uppercase tracking-wide mb-2">
                Nøglebegreber
              </h4>
              <div className="flex flex-wrap gap-2">
                {topic.keyConcepts.map((concept, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 text-xs bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full"
                  >
                    {concept}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Subtopics */}
          {topic.subtopics && topic.subtopics.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-neutral-medium uppercase tracking-wide mb-2">
                Underemner
              </h4>
              <div className="space-y-2">
                {topic.subtopics.map((subtopic, subIdx) => (
                  <div
                    key={subIdx}
                    className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg group"
                  >
                    <span className="text-gray-400">•</span>
                    <EditableText
                      value={subtopic.title}
                      onChange={(newTitle) => handleSubtopicTitleChange(subIdx, newTitle)}
                      className="flex-1 text-sm text-neutral-dark dark:text-gray-200"
                    />
                    {subtopic.concepts && subtopic.concepts.length > 0 && (
                      <span className="text-xs text-neutral-medium">
                        ({subtopic.concepts.length} begreber)
                      </span>
                    )}
                    <button
                      onClick={() => handleDeleteSubtopic(subIdx)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 rounded transition-all"
                      title="Slet underemne"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Duration */}
          {topic.estimatedDuration && (
            <p className="text-xs text-neutral-medium mt-3">
              Estimeret tid: ~{topic.estimatedDuration} minutter
            </p>
          )}
        </div>
      )}
    </Card>
  );
};

const LearningPlanPreview: React.FC<LearningPlanPreviewProps> = ({
  plan,
  onApprove,
  onEditWithAnna,
  onCancel,
  isApproving = false,
}) => {
  // Local state for edited plan
  const [editedPlan, setEditedPlan] = useState<SuggestedLearningPlan>(plan);

  // Track which topics are expanded
  const [expandedTopics, setExpandedTopics] = useState<Set<number>>(new Set([0]));

  // Toggle topic expansion
  const toggleTopic = useCallback((index: number) => {
    setExpandedTopics((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  // Update a topic
  const handleUpdateTopic = useCallback((index: number, updatedTopic: SuggestedPlanTopic) => {
    setEditedPlan((prev) => ({
      ...prev,
      topics: prev.topics.map((t, i) => (i === index ? updatedTopic : t)),
    }));
  }, []);

  // Delete a topic
  const handleDeleteTopic = useCallback((index: number) => {
    setEditedPlan((prev) => ({
      ...prev,
      topics: prev.topics.filter((_, i) => i !== index),
    }));
  }, []);

  // Calculate totals
  const totalTopics = editedPlan.topics.length;
  const totalSubtopics = editedPlan.topics.reduce(
    (sum, t) => sum + (t.subtopics?.length || 0),
    0
  );
  const totalSessions = editedPlan.topics.reduce((sum, t) => {
    const sessions = t.estimatedDuration
      ? Math.ceil(t.estimatedDuration / 30)
      : t.subtopics?.length || 1;
    return sum + sessions;
  }, 0);

  // Handle approve
  const handleApprove = () => {
    onApprove(editedPlan);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-neutral-dark dark:text-white mb-1">
            Din emneplan
          </h2>
          <p className="text-neutral-medium">{editedPlan.summary}</p>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card padding="md" className="text-center">
          <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
            {totalTopics}
          </p>
          <p className="text-sm text-neutral-medium">Emner</p>
        </Card>
        <Card padding="md" className="text-center">
          <p className="text-2xl font-bold text-secondary-600 dark:text-secondary-400">
            {totalSubtopics}
          </p>
          <p className="text-sm text-neutral-medium">Underemner</p>
        </Card>
        <Card padding="md" className="text-center">
          <p className="text-2xl font-bold text-accent-600 dark:text-accent-400">
            ~{totalSessions}
          </p>
          <p className="text-sm text-neutral-medium">Samtaler</p>
        </Card>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start gap-3">
          <span className="text-xl">💡</span>
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium mb-1">Tip: Du kan redigere planen</p>
            <p className="text-blue-700 dark:text-blue-400">
              Klik på emne- eller underemne-titler for at redigere dem direkte.
              Du kan også slette emner eller underemner, der ikke er relevante.
            </p>
          </div>
        </div>
      </div>

      {/* Topics List */}
      <div className="space-y-3">
        {editedPlan.topics.map((topic, index) => (
          <TopicCard
            key={index}
            topic={topic}
            index={index}
            isExpanded={expandedTopics.has(index)}
            onToggle={() => toggleTopic(index)}
            onUpdate={(updated) => handleUpdateTopic(index, updated)}
            onDelete={() => handleDeleteTopic(index)}
          />
        ))}
      </div>

      {/* Empty State */}
      {editedPlan.topics.length === 0 && (
        <Card padding="lg" className="text-center">
          <span className="text-4xl mb-3 block">📭</span>
          <p className="text-neutral-medium">
            Ingen emner i planen. Bed Anna om at generere en ny plan.
          </p>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={isApproving} className="sm:order-1">
            Annuller
          </Button>
        )}
        <Button
          variant="outline"
          onClick={onEditWithAnna}
          disabled={isApproving}
          className="sm:order-2 flex items-center gap-2"
        >
          💬 Rediger med Anna
        </Button>
        <Button
          variant="primary"
          onClick={handleApprove}
          disabled={editedPlan.topics.length === 0 || isApproving}
          className="sm:order-3 sm:ml-auto flex items-center gap-2 bg-green-600 hover:bg-green-700"
        >
          {isApproving ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Opretter emner...
            </>
          ) : (
            <>✅ Godkend emneplan</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default LearningPlanPreview;
